///
/// EarthBrowser
///
/// SynopticDataFrame - Holds pressure centers for a time step
///
/// Created by Matt Giger on 1/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type DatasetFrame } from "@/earthbrowser/datasets/DatasetSeries"
import { type timerange } from "@stovokor/math/time"
import { SynopticTable, type PressureCenterFeature } from "./SynopticTable"
import { type IsobarLabel } from "./IsobarLabelExtractor"
import { type FrontFeature } from "../weather-grid/FrontDetector"
import { type JetMaximum } from "../weather-grid/JetStreamFieldGenerator"
import { type AxisFeature } from "../weather-grid/TroughRidgeDetector"

/**
 * SynopticDataFrame - Contains pressure centers for a single time step
 *
 * Implements DatasetFrame for use with DatasetSeries time-based animation
 */
export class SynopticDataFrame implements DatasetFrame {
	public url: string  // Used as unique identifier (includes checkpoint to avoid cross-checkpoint reuse)
	public readonly validTime: string
	public range: timerange
	public loading: boolean = false
	public loaded: boolean = false

	// Data loaded from database
	public pressureCenters: PressureCenterFeature[] = []

	// Label anchors along isobars, computed from the pressure grid
	public isobarLabels: IsobarLabel[] = []

	// Label anchors along the 500 hPa height contours (dam)
	public heightLabels: IsobarLabel[] = []

	// Jet streak cores (250 hPa speed maxima) to label on the jet shell
	public jetMaxima: JetMaximum[] = []

	// 500 hPa trough / ridge axes
	public heightAxes: AxisFeature[] = []

	// Weather fronts detected from the 850 hPa theta-e grid
	public fronts: FrontFeature[] = []

	constructor(validTime: string, range: timerange, checkpointVersion: string) {
		this.validTime = validTime
		this.url = `${checkpointVersion}:${validTime}`
		this.range = range
	}

	async load(): Promise<void> {
		if (this.loaded || this.loading) {
			return
		}

		this.loading = true
		try {
			// Load pressure centers from database
			this.pressureCenters = await SynopticTable.getPressureCentersForTime(this.validTime)
			this.loaded = true
		} catch (error) {
			console.error(`[SynopticDataFrame] Failed to load data for ${this.url}:`, error)
			this.loaded = false
		} finally {
			this.loading = false
		}
	}

	public purge(): void {
		this.loaded = false
		this.pressureCenters = []
		this.isobarLabels = []
		this.heightLabels = []
		this.jetMaxima = []
		this.heightAxes = []
		this.fronts = []
	}
}
